"use client"

import Image from "next/image"
import { useState } from "react"
import type { components } from "umbraco"
import { getLinkUrl, getMediaUrl } from "umbraco"
import { getNonUmbracoUrl } from "utils"
import { getUmbracoMediaUrl } from "utils/getUmbracoMediaUrl"
import ArrowLeft from "../../icons/ArrowLeft"
import MintLogo from "../../icons/MintLogo"
import Minus from "../../icons/Minus"
import Plus from "../../icons/Plus"
import {
  AccordionContent,
  AccordionHeader,
  AccordionIconWrapper,
  AccordionItem,
  AccordionLinkRow,
  AccordionTitle,
  AdditionalSitesLabel,
  AdditionalSitesMobileLinks,
  BackgroundVideo,
  BottomBar,
  BottomFooter,
  BottomFooterFixed,
  BottomFooterBgDesktop,
  BottomFooterBgMobile,
  BottomFooterContent,
  BottomFooterOverlay,
  BottomLogoImage,
  CopyrightText,
  CreatedByRow,
  CreatedByText,
  CtaInternalLinkRow,
  CtaLinkRow,
  CtaLinkText,
  EmailLink,
  EmailLinkMobile,
  EmailText,
  FooterContainer,
  FooterRoot,
  LegalLink,
  LegalLinksRow,
  LegalLinkText,
  LogoImageWrapper,
  LogoLink,
  MobileAccordion,
  NavColumn,
  NavColumns,
  NavColumnTitle,
  NavRow,
  NavLink,
  NavLinkText,
  SocialIconLink,
  SocialLinksRow,
  UpperFooter,
  UpperFooterInner,
} from "./styles"

type FooterProperties = components["schemas"]["FooterPropertiesModel"]
type ApiLinkModel = components["schemas"]["ApiLinkModel"]

/**
 * Social Link Item element properties.
 *
 * The CMS `socialLinkItem` element type exposes an `icon` Media Picker, a
 * `link` Link picker and a `label` text field (EGDE-35). The icon image is
 * authored per item in Umbraco rather than mapped from a hard-coded platform
 * key, so the footer renders whatever icon the editor selected.
 */
interface SocialLinkItemProperties {
  icon?: components["schemas"]["IApiMediaWithCropsModel"][] | null
  link?: ApiLinkModel[] | null
  // EGDE-310: platform is a single-select dropdown (stored as a string array);
  // it replaces the old free-text `label` as the accessible label source.
  platform?: string[] | null
  label?: string | null
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Detect whether a media picker value points to a video file. */
function isVideoMedia(mediaPicker: unknown): boolean {
  if (!Array.isArray(mediaPicker) || mediaPicker.length === 0) return false
  const media = mediaPicker[0] as any
  const mediaType = (media?.mediaType ?? "").toLowerCase()
  const extension = (media?.extension ?? "").toLowerCase()
  const url = (media?.url ?? "").toLowerCase()
  const videoExtensions = ["mp4", "webm", "ogg", "mov"]
  // Umbraco's Delivery API reports the media type alias ("umbracoMediaVideo"),
  // not a bare "video", and frequently omits `extension` on nested media-picker
  // values — so also derive the type from the file URL.
  return (
    mediaType.includes("video") ||
    videoExtensions.includes(extension) ||
    videoExtensions.some((ext) => url.endsWith(`.${ext}`))
  )
}

/**
 * Resolve the URL from an ApiLinkModel.
 * For Content links, applies getNonUmbracoUrl to strip /pages/ prefix.
 * For External links, returns the url as-is.
 */
function resolveApiLinkUrl(link: ApiLinkModel): string {
  const rawUrl = link.url ?? link.route?.path ?? null
  if (!rawUrl) return "#"

  if (link.linkType === "Content") {
    return getNonUmbracoUrl(rawUrl)
  }
  return rawUrl
}

export interface MenuItemData {
  title: string
  link: string | null
  /** EGDE-351: link target ("_blank" opens in a new tab) from the CMS picker. */
  target?: string
  children?: MenuItemData[]
}

export interface FooterData {
  properties: FooterProperties | null
  menuItems: MenuItemData[]
}

interface FooterProps {
  data: FooterProperties
  menuData?: MenuItemData[]
}

/** Build accordion items from menu data: items with children + items without children as a group + additional sites */
function buildAccordionSections(
  menuItems: MenuItemData[],
  additionalSitesLabel: string | null | undefined,
  additionalSitesLinks:
    | components["schemas"]["ApiLinkModel"][]
    | null
    | undefined,
) {
  const sections: {
    title: string
    link: string | null
    target?: string
    children?: {
      title: string
      link: string | null
      target?: string
      isCta?: boolean
      isExternal?: boolean
    }[]
  }[] = []

  // Items WITH children get their own accordion section
  for (const item of menuItems) {
    if (item.children && item.children.length > 0) {
      sections.push({
        title: item.title,
        link: item.link,
        target: item.target,
        children: item.children.map((c) => ({
          title: c.title,
          link: c.link,
          target: c.target,
        })),
      })
    }
  }

  // Items WITHOUT children form a "pages" group — each is a top-level accordion row
  for (const item of menuItems) {
    if (!item.children || item.children.length === 0) {
      sections.push({
        title: item.title,
        link: item.link,
        target: item.target,
      })
    }
  }

  // Additional sites section
  if (
    additionalSitesLabel &&
    additionalSitesLinks &&
    additionalSitesLinks.length > 0
  ) {
    sections.push({
      title: additionalSitesLabel,
      link: null,
      children: additionalSitesLinks.map((l) => ({
        title: l.title ?? "",
        link: resolveApiLinkUrl(l),
        isCta: true,
        isExternal: l.linkType === "External",
      })),
    })
  }

  return sections
}

export default function Footer({ data, menuData = [] }: FooterProps) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null)

  // Logo
  const logoUrl = getMediaUrl(data.logo)
  const logoSrc = logoUrl ? getUmbracoMediaUrl(logoUrl) : null
  // Logo link — use CMS field if available, otherwise default to homepage
  const logoLinkRaw = getLinkUrl(data.footerLogoLink)
  const logoHref = logoLinkRaw ? getNonUmbracoUrl(logoLinkRaw) : "/"

  // Bottom logo
  const logoBottomUrl = getMediaUrl(data.logoBottom)
  const logoBottomSrc = logoBottomUrl ? getUmbracoMediaUrl(logoBottomUrl) : null

  // Background media (image or video) — authored separately per viewport.
  const bgDesktopUrl = getMediaUrl(data.backgroundImageDesktop)
  const bgDesktopSrc = bgDesktopUrl ? getUmbracoMediaUrl(bgDesktopUrl) : null
  const bgDesktopIsVideo = isVideoMedia(data.backgroundImageDesktop)

  // EGDE-266: mobile can use its own background asset. Only render a separate
  // mobile wrapper when a *distinct* mobile asset is authored — otherwise the
  // single desktop wrapper is shown on all viewports, so a shared asset (the
  // common case) is never fetched/decoded twice.
  const bgMobileUrl = getMediaUrl(data.backgroundImageMobile)
  const bgMobileSrc = bgMobileUrl ? getUmbracoMediaUrl(bgMobileUrl) : null
  const bgMobileIsVideo = isVideoMedia(data.backgroundImageMobile)
  const hasDistinctMobileBg = !!bgMobileSrc && bgMobileSrc !== bgDesktopSrc
  // Menu items split
  const pageLinks = menuData.filter(
    (item) => !item.children || item.children.length === 0,
  )
  const serviceMenuItems = menuData.filter(
    (item) => item.children && item.children.length > 0,
  )

  // Social links from block list
  const socialItems = data.socialLinks?.items ?? []

  // Legal links
  const legalLinks = data.legalLinks ?? []

  // Additional sites
  const additionalSitesLabel = data.additionalSitesLabel
  const additionalSitesLinks = data.additionalSitesLinks ?? []

  // Mobile accordion sections
  const accordionSections = buildAccordionSections(
    menuData,
    additionalSitesLabel,
    data.additionalSitesLinks,
  )

  const handleAccordionToggle = (index: number) => {
    setExpandedIndex((prev) => (prev === index ? null : index))
  }

  return (
    <FooterRoot data-component="footer">
      <UpperFooter>
        <FooterContainer>
          <UpperFooterInner>
            {/* EGDE-487: NavRow keeps the logo stacked above the columns up to
                1920, where the Figma footer places it inline at their
                inline-start edge instead. */}
            <NavRow>
              {logoSrc && (
                <LogoLink href={logoHref}>
                  <LogoImageWrapper>
                    <Image
                      src={logoSrc}
                      alt="Egged Plus"
                      fill
                      sizes="(min-width: 1280px) 188px, 138px"
                      style={{ objectFit: "contain" }}
                    />
                  </LogoImageWrapper>
                </LogoLink>
              )}

              <NavColumns>
                <NavColumn>
                  {pageLinks.map((item, i) => (
                    <NavLink
                      key={i}
                      href={getNonUmbracoUrl(item.link ?? undefined)}
                      target={item.target}
                      rel={
                        item.target === "_blank"
                          ? "noopener noreferrer"
                          : undefined
                      }
                    >
                      <NavColumnTitle variant="h3" component="span">
                        {item.title}
                      </NavColumnTitle>
                    </NavLink>
                  ))}
                </NavColumn>

                {serviceMenuItems.map((item, i) => (
                  <NavColumn key={i}>
                    <NavColumnTitle variant="h3">{item.title}</NavColumnTitle>
                    {item.children?.map((child, j) => (
                      <NavLink
                        key={j}
                        href={getNonUmbracoUrl(child.link ?? undefined)}
                        target={child.target}
                        rel={
                          child.target === "_blank"
                            ? "noopener noreferrer"
                            : undefined
                        }
                      >
                        <NavLinkText variant="body1">{child.title}</NavLinkText>
                      </NavLink>
                    ))}
                  </NavColumn>
                ))}

                {additionalSitesLinks.length > 0 && (
                  <NavColumn>
                    {additionalSitesLabel && (
                      <AdditionalSitesLabel variant="body1">
                        {additionalSitesLabel}
                      </AdditionalSitesLabel>
                    )}
                    {additionalSitesLinks.map((link, i) => {
                      const href = resolveApiLinkUrl(link)
                      const isExternal = link.linkType === "External"
                      return isExternal ? (
                        <CtaLinkRow
                          key={i}
                          href={href}
                          target={link.target ?? "_blank"}
                          rel="noopener noreferrer"
                        >
                          <CtaLinkText variant="body1">
                            {link.title}
                          </CtaLinkText>
                          <ArrowLeft />
                        </CtaLinkRow>
                      ) : (
                        <CtaInternalLinkRow key={i} href={href}>
                          <CtaLinkText variant="body1">
                            {link.title}
                          </CtaLinkText>
                          <ArrowLeft />
                        </CtaInternalLinkRow>
                      )
                    })}
                  </NavColumn>
                )}
              </NavColumns>
            </NavRow>

            <MobileAccordion>
              {accordionSections.map((section, index) => {
                const hasChildren =
                  section.children && section.children.length > 0
                const isExpanded = expandedIndex === index

                // EGDE-41: Additional sites section (isCta children) — no accordion, always visible
                const isAdditionalSites =
                  hasChildren && section.children?.some((child) => child.isCta)

                if (!hasChildren) {
                  // Simple link row (no accordion)
                  return (
                    <AccordionItem key={index}>
                      <AccordionLinkRow
                        href={getNonUmbracoUrl(section.link ?? undefined)}
                        target={section.target}
                        rel={
                          section.target === "_blank"
                            ? "noopener noreferrer"
                            : undefined
                        }
                      >
                        <AccordionTitle variant="h6">
                          {section.title}
                        </AccordionTitle>
                      </AccordionLinkRow>
                    </AccordionItem>
                  )
                }

                if (isAdditionalSites) {
                  // EGDE-41/159: Additional sites — always visible, no accordion wrapper
                  return (
                    <AccordionItem key={index}>
                      <AdditionalSitesLabel variant="body1">
                        {section.title}
                      </AdditionalSitesLabel>
                      <AdditionalSitesMobileLinks>
                        {section.children?.map((child, j) => (
                          <CtaLinkRow
                            key={j}
                            href={child.link ?? "#"}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <CtaLinkText variant="body2">
                              {child.title}
                            </CtaLinkText>
                            <ArrowLeft />
                          </CtaLinkRow>
                        ))}
                      </AdditionalSitesMobileLinks>
                    </AccordionItem>
                  )
                }

                return (
                  <AccordionItem key={index}>
                    <AccordionHeader
                      onClick={() => handleAccordionToggle(index)}
                      aria-expanded={isExpanded}
                    >
                      <AccordionTitle variant="h6">
                        {section.title}
                      </AccordionTitle>
                      <AccordionIconWrapper $expanded={isExpanded}>
                        {isExpanded ? (
                          <Minus width={16} height={16} />
                        ) : (
                          <Plus width={16} height={16} />
                        )}
                      </AccordionIconWrapper>
                    </AccordionHeader>
                    <AccordionContent $expanded={isExpanded}>
                      {section.children?.map((child, j) =>
                        child.isCta && child.isExternal ? (
                          <CtaLinkRow
                            key={j}
                            href={child.link ?? "#"}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <CtaLinkText variant="body2">
                              {child.title}
                            </CtaLinkText>
                            <ArrowLeft />
                          </CtaLinkRow>
                        ) : child.isCta ? (
                          <CtaInternalLinkRow key={j} href={child.link ?? "#"}>
                            <CtaLinkText variant="body2">
                              {child.title}
                            </CtaLinkText>
                            <ArrowLeft />
                          </CtaInternalLinkRow>
                        ) : (
                          <NavLink
                            key={j}
                            href={getNonUmbracoUrl(child.link ?? undefined)}
                            target={child.target}
                            rel={
                              child.target === "_blank"
                                ? "noopener noreferrer"
                                : undefined
                            }
                          >
                            <NavLinkText variant="body2">
                              {child.title}
                            </NavLinkText>
                          </NavLink>
                        ),
                      )}
                    </AccordionContent>
                  </AccordionItem>
                )
              })}
            </MobileAccordion>

            {/* EGDE-30/42: On mobile — legal links (order 1), social (order 2), email (order 3) */}
            {/* On desktop — legal links + email inline, then social icons */}
            <BottomBar>
              {(legalLinks.length > 0 || data.contactEmail) && (
                <LegalLinksRow>
                  {legalLinks.map((link, i) => (
                    <LegalLink
                      key={i}
                      href={getNonUmbracoUrl(
                        link.url ?? (link as any).route?.path,
                      )}
                    >
                      <LegalLinkText variant="body2">
                        {link.title}
                      </LegalLinkText>
                    </LegalLink>
                  ))}
                  {/* EGDE-30: On desktop the email is the LAST item in the
                      legal-links row, flush after the privacy-policy link
                      (shares the 12px divider rhythm) — matches Figma which
                      places the email at the inline-end next to privacy.
                      Hidden on mobile — the mobile email renders below the
                      social row. */}
                  {data.contactEmail && (
                    <EmailLink href={`mailto:${data.contactEmail}`}>
                      <EmailText variant="body2">{data.contactEmail}</EmailText>
                    </EmailLink>
                  )}
                </LegalLinksRow>
              )}

              {socialItems.length > 0 && (
                <SocialLinksRow>
                  {socialItems.map((item, i) => {
                    const props = item.content?.properties as
                      | SocialLinkItemProperties
                      | undefined
                    if (!props) return null

                    // EGDE-35: render the icon image authored in the CMS
                    // (Media Picker), not a hard-coded platform SVG.
                    const iconUrl = getMediaUrl(props.icon)
                    const iconSrc = iconUrl ? getUmbracoMediaUrl(iconUrl) : null
                    if (!iconSrc) return null

                    const linkUrl = getLinkUrl(props.link)
                    const linkTarget =
                      (props.link as any)?.[0]?.target ?? "_blank"
                    // EGDE-310: prefer the platform dropdown, fall back to the
                    // legacy free-text label, then a generic label.
                    // EGDE-348: a single-select DropDown.Flexible is a STRING in
                    // the Delivery API, so `platform?.[0]` took the first CHARACTER
                    // (e.g. "W") — normalise array/string so the label matches Umbraco.
                    const platformRaw = (
                      props as { platform?: string[] | string | null }
                    ).platform
                    const platformValue = Array.isArray(platformRaw)
                      ? platformRaw[0]
                      : platformRaw
                    const label = platformValue || props.label || "Social link"

                    return (
                      <SocialIconLink
                        key={i}
                        href={linkUrl ?? "#"}
                        target={linkTarget}
                        rel="noopener noreferrer"
                        aria-label={label}
                      >
                        <img src={iconSrc} alt={label} />
                      </SocialIconLink>
                    )
                  })}
                </SocialLinksRow>
              )}

              {/* EGDE-30/42: Mobile-only email — appears below the social row.
                  Hidden on desktop where the in-row email is shown instead. */}
              {data.contactEmail && (
                <EmailLinkMobile href={`mailto:${data.contactEmail}`}>
                  <EmailText variant="body2">{data.contactEmail}</EmailText>
                </EmailLinkMobile>
              )}
            </BottomBar>
          </UpperFooterInner>
        </FooterContainer>
      </UpperFooter>

      <BottomFooter>
        <BottomFooterFixed>
          {bgDesktopSrc && (
            <BottomFooterBgDesktop $allViewports={!hasDistinctMobileBg}>
              {bgDesktopIsVideo ? (
                <BackgroundVideo
                  src={bgDesktopSrc}
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="auto"
                />
              ) : (
                <Image
                  src={bgDesktopSrc}
                  alt={data.backgroundImageAltText ?? ""}
                  fill
                  sizes="100vw"
                  style={{ objectFit: "cover" }}
                />
              )}
            </BottomFooterBgDesktop>
          )}
          {hasDistinctMobileBg && (
            <BottomFooterBgMobile>
              {bgMobileIsVideo ? (
                <BackgroundVideo
                  src={bgMobileSrc}
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="auto"
                />
              ) : (
                <Image
                  src={bgMobileSrc}
                  alt={data.backgroundImageAltText ?? ""}
                  fill
                  sizes="100vw"
                  style={{ objectFit: "cover" }}
                />
              )}
            </BottomFooterBgMobile>
          )}
          <BottomFooterOverlay />

          <BottomFooterContent>
            {logoBottomSrc && (
              <BottomLogoImage src={logoBottomSrc} alt="Egged Plus" />
            )}

            {data.copyrightText && (
              <CopyrightText variant="body2">
                © {data.copyrightText}
              </CopyrightText>
            )}

            <CreatedByRow>
              <MintLogo width={60} height={20} style={{ flexShrink: 0 }} />
              <CreatedByText variant="body1">Created By</CreatedByText>
            </CreatedByRow>
          </BottomFooterContent>
        </BottomFooterFixed>
      </BottomFooter>
    </FooterRoot>
  )
}
